/**
 * Image URL helpers — optimise image URLs for the brand site.
 *
 * Supports two source types:
 *   1. Unsplash (images.unsplash.com) — imgix-style query params
 *      (`?auto=format&fit=crop&w=…&q=…&fm=webp`).
 *   2. Backend CMS media (`/api/cms/media/{filename}`) — the FastAPI
 *      `cms.serve_media` route accepts the same `?w=` / `?fmt=webp` and
 *      returns a server-side-resized, cached WebP.
 *
 * Other URLs (data:, third-party) are returned untouched.
 */

const UNSPLASH_RE = /(^https?:\/\/images\.unsplash\.com\/)/i;
const CMS_MEDIA_RE = /\/api\/cms\/media\//i;

const isUnsplash = (url) => !!url && UNSPLASH_RE.test(url);
const isCmsMedia = (url) => !!url && CMS_MEDIA_RE.test(url);

/**
 * Build an optimised URL at the target width.
 *
 *   smartImg(src, 800)              // ↪ JPEG/WebP at 800w
 *   smartImg(src, 1200, { q: 70 })  // ↪ ditto, quality 70
 */
export const smartImg = (url, w = 1200, { q = 75 } = {}) => {
  if (!url) return url;
  if (isUnsplash(url)) {
    const base = url.split('?')[0];
    return `${base}?auto=format&fit=crop&w=${w}&q=${q}&fm=webp`;
  }
  if (isCmsMedia(url)) {
    // Backend honours `?w=` and `?fmt=webp` and caches the result on disk.
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}w=${w}&fmt=webp&q=${q}`;
  }
  return url;
};

/**
 * Build a responsive srcset (`url 480w, url 800w, ...`). Returns '' for URLs
 * we can't resize so call sites should pass `srcset || undefined`.
 */
export const smartSrcSet = (url, widths = [480, 800, 1200, 1600], { q = 75 } = {}) => {
  if (!url) return '';
  if (!isUnsplash(url) && !isCmsMedia(url)) return '';
  return widths.map((w) => `${smartImg(url, w, { q })} ${w}w`).join(', ');
};

// Back-compat aliases — old call sites can keep using `unsplash` / `unsplashSrcSet`.
export const unsplash = smartImg;
export const unsplashSrcSet = smartSrcSet;
